<template>
  <!-- The app's call overlay (Workspace369 origin/main, Oct 22 2026; lib/view/inbox/widgets/call/): CallOverlayHost
       floats over whatever screen is open without dimming it. Wide windows get _CallOverlayDesktop, the 360pt
       card 12pt from the bottom-right corner (floating_call_geometry.dart): the status line and caller, then
       Decline / Answer while it rings, or CallCardControls once answered (Mute, Keypad, Hold, Record, Transfer
       tiles and End call); the transfer box is the pending state while a teammate rings. Phones get
       _CallOverlayMobile, the full-screen dark call screen with round controls; a transfer swaps them for the
       pending status (call_actions.dart _buildPendingTransfer). Strings are app_en.arb's; the
       caller and teammate are sample data. Recording is on from the connect, as when the workspace records
       calls; the tile keeps its "Record" label and turns red. Radius exceptions, all the app's own: the phone's
       round buttons and avatar, the status dots, the phone's transfer box (16) and Cancel transfer (22). -->
  <div class="cxo" :class="[`cxo--${state}`, phone ? 'cxo--phone' : 'cxo--card']">
    <svg class="cxo-sprite" width="0" height="0">
      <symbol v-for="(d, name) in icons" :id="`cxo-${name}`" :key="name" viewBox="0 0 24 24"><path :d="d" /></symbol>
    </svg>

    <!-- Desktop / tablet card -->
    <div v-if="!phone" class="cxo-card">
      <div class="cxo-head">
        <span class="cxo-who">
          <span class="cxo-status"><i :class="{ on: state !== 'ringing' }" />{{ statusText }}</span>
          <b>{{ caller }}</b>
          <small>{{ number }}</small>
        </span>
        <span class="cxo-collapse"><svg class="ic"><use href="#cxo-collapseChevron" /></svg></span>
      </div>
      <div v-if="state === 'ringing'" class="cxo-ring">
        <span class="cxo-btn red"><svg class="ic"><use href="#cxo-callEnd" /></svg>Decline</span>
        <span class="cxo-btn green"><svg class="ic"><use href="#cxo-callIcon" /></svg>Answer</span>
      </div>
      <template v-else>
        <div class="cxo-tiles">
          <span v-for="t in tiles" :key="t.k" class="cxo-tile" :class="t.tone"><svg v-if="t.i" class="ic"><use :href="`#cxo-${t.i}`" /></svg><i v-else class="cxo-rec" />{{ t.k }}</span>
        </div>
        <div v-if="state === 'transfer'" class="cxo-pending">
          <div class="cxo-pend-box">
            <i class="cxo-spin" />
            <span><b>Ringing Jordan Lee…</b><small>Your call is still active</small></span>
            <time>0:24</time>
          </div>
          <span class="cxo-btn quiet"><svg class="ic"><use href="#cxo-close" /></svg>Cancel transfer</span>
        </div>
        <div class="cxo-end"><span class="cxo-btn red"><svg class="ic"><use href="#cxo-callEnd" /></svg>End call</span></div>
      </template>
    </div>

    <!-- Phone: full screen -->
    <div v-else class="cxo-screen">
      <span class="cxo-min"><svg class="ic"><use href="#cxo-collapseChevron" /></svg></span>
      <div class="cxo-id">
        <span class="cxo-pstatus"><i />{{ statusText }}</span>
        <span class="cxo-avatar">M</span>
        <b>{{ caller }}</b>
        <small>{{ number }}</small>
      </div>
      <div v-if="state === 'ringing'" class="cxo-pring">
        <span class="cxo-round red"><svg class="ic"><use href="#cxo-callEnd" /></svg></span>
        <span class="cxo-round green"><svg class="ic"><use href="#cxo-callIcon" /></svg></span>
      </div>
      <!-- A transfer swaps the controls for the pending status (call_actions.dart _buildPendingTransfer). -->
      <div v-else-if="state === 'transfer'" class="cxo-ptr">
        <div class="cxo-ptr-head"><svg class="ic"><use href="#cxo-arrowBackIos" /></svg>Transfer status</div>
        <div class="cxo-ptr-box">
          <svg class="ic"><use href="#cxo-ringVolume" /></svg>
          <b>Ringing Jordan Lee…</b>
          <small>Your call is still active</small>
          <span class="cxo-ptr-wait"><i />0:24</span>
        </div>
        <span class="cxo-ptr-cancel">Cancel transfer</span>
        <span class="cxo-round red cxo-ptr-end"><svg class="ic"><use href="#cxo-callEnd" /></svg></span>
      </div>
      <div v-else class="cxo-pctl">
        <span v-for="c in phoneControls" :key="c.k" class="cxo-pc" :class="{ on: c.on }"><span><svg v-if="c.i" class="ic"><use :href="`#cxo-${c.i}`" /></svg><i v-else class="cxo-rec" /></span>{{ c.k }}</span>
        <span class="cxo-round red cxo-pend"><svg class="ic"><use href="#cxo-callEnd" /></svg></span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { callIcons } from "~/data/app-icons-call.js";
import { postCallIcons } from "~/data/app-icons-postcall.js";

const props = defineProps({
  // ringing | oncall | transfer
  state: { type: String, default: "transfer" },
  phone: { type: Boolean, default: false },
});

const icons = { ...callIcons, callEnd: postCallIcons.callEnd, close: postCallIcons.close };
const caller = "Maya Ortiz";
const number = "+1 (512) 555-0182";
const statusText = computed(() => ({ ringing: "Incoming call", oncall: "On call · 00:42", transfer: "On call · 02:31" })[props.state]);
const tiles = computed(() => [
  { k: "Mute", i: "micNone" },
  { k: "Keypad", i: "dialpadIcon" },
  { k: "Hold", i: "pauseIcon" },
  { k: "Record", tone: "red" },
  { k: "Transfer", i: "phoneForwarded", tone: props.state === "transfer" ? "blue" : "" },
]);
const phoneControls = computed(() => [
  { k: "Mute", i: "micNone" },
  { k: "Record", on: true },
  { k: "Hold", i: "pauseIcon" },
  { k: "Keypad", i: "dialpadIcon" },
  { k: "Speaker", i: "volumeUp" },
  { k: "Transfer", i: "swapCalls", on: props.state === "transfer" },
]);
</script>

<style>
.cxo { --ink: rgb(10 10 10 / 0.9); --ink2: rgb(10 10 10 / 0.6); --ink3: rgb(10 10 10 / 0.4); --bg: #fff; --hair: rgb(10 10 10 / 0.08);
  --green: #2d7a4e; --red-t: #ba3a37; --red-f: #fbeaea; --blue-t: #3f58bf; --blue-f: #ebf0fd; --tile: rgb(10 10 10 / 0.78);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif; font-variant-numeric: tabular-nums; line-height: 1.3; text-align: left; }
.dark .cxo { --ink: rgb(250 250 250 / 0.9); --ink2: rgb(250 250 250 / 0.6); --ink3: rgb(250 250 250 / 0.4); --bg: #1a1a1a; --hair: rgb(250 250 250 / 0.12);
  --green: #6ed39a; --red-t: #f69591; --red-f: rgb(229 83 78 / 0.2); --blue-t: #93aef9; --blue-f: rgb(91 127 234 / 0.2); --tile: rgb(250 250 250 / 0.78); }
.cxo-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
.cxo .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; }

/* ---------- the floating card ---------- */
.cxo--card { position: absolute; z-index: 6; right: 12px; bottom: 12px; width: min(360px, calc(100% - 24px)); }
.cxo-card {
  overflow: hidden; border: 1px solid var(--hair); border-radius: 8px; background: var(--bg); color: var(--ink); font-size: 13px;
  box-shadow: 2px 4px 13px rgb(0 0 0 / 0.16), 0 2px 2.6px rgb(0 0 0 / 0.08); animation: cxo-up 0.45s ease-out;
}
.dark .cxo-card { box-shadow: 2px 4px 13px rgb(0 0 0 / 0.4), 0 2px 2.6px rgb(0 0 0 / 0.24); }
.cxo-head { display: flex; align-items: flex-start; padding: 14px 8px 8px 12px; }
.cxo-who { display: grid; flex: 1; min-width: 0; }
.cxo-status { display: flex; align-items: center; gap: 6px; color: var(--ink2); font-size: 12px; }
.cxo-status i { width: 6px; height: 6px; border-radius: 50%; background: var(--green); }
.cxo-who b { margin-top: 6px; font-size: 16px; font-weight: 500; }
.cxo-who small { margin-top: 2px; color: var(--ink2); font-size: 13px; }
.cxo-collapse { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 6px; color: var(--ink2); }
.cxo-ring { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; border-top: 1px solid var(--hair); padding: 12px; }
.cxo-btn { display: flex; align-items: center; justify-content: center; gap: 6px; height: 44px; border-radius: 8px; font-weight: 500; white-space: nowrap; }
.cxo-btn.red { background: #e5484d; color: #fff; }
.cxo-btn.green { background: #30a46c; color: #fff; }
.cxo-btn.quiet { height: 32px; margin-top: 8px; border: 1px solid var(--hair); color: var(--ink); }
.cxo-btn .ic { width: 16px; height: 16px; }
.cxo-tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 6px; padding: 12px 10px 0 12px; animation: cxo-in 0.4s ease-out; }
.cxo-tile { display: grid; justify-items: center; align-content: center; gap: 5px; height: 60px; border: 1px solid var(--hair); border-radius: 6px; color: var(--tile); font-size: 12px; font-weight: 500; }
.cxo-tile .ic { width: 20px; height: 20px; color: var(--ink); }
.cxo-tile.red { border-color: transparent; background: var(--red-f); color: var(--red-t); }
.cxo-tile.blue { border-color: transparent; background: var(--blue-f); color: var(--blue-t); }
.cxo-tile.blue .ic { color: var(--blue-t); }
.cxo-rec { width: 14px; height: 14px; margin: 3px; border-radius: 50%; background: currentColor; }
.cxo-tile .cxo-rec { color: var(--red-t); }
.cxo-pending { padding: 10px 12px 0; animation: cxo-in 0.45s ease-out; }
.cxo-pend-box { display: flex; align-items: center; gap: 10px; border: 1px solid var(--hair); border-radius: 8px; padding: 12px; }
.cxo-pend-box span { display: grid; flex: 1; gap: 2px; }
.cxo-pend-box b { font-weight: 500; }
.cxo-pend-box small, .cxo-pend-box time { color: var(--ink2); font-size: 12px; }
.cxo-spin { flex: none; width: 14px; height: 14px; border: 2px solid var(--hair); border-top-color: var(--ink); border-radius: 50%; animation: cxo-spin 0.9s linear infinite; }
.cxo-end { padding: 10px 12px 12px; }
@keyframes cxo-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes cxo-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes cxo-spin { to { transform: rotate(360deg); } }

/* ---------- phone: the full-screen call ---------- */
.cxo--phone { position: absolute; z-index: 6; inset: 0; }
.cxo-screen {
  --primary: #171717; position: relative; display: flex; flex-direction: column; align-items: center; height: 100%; overflow: hidden; color: #fff;
  background: radial-gradient(calc(var(--u) * 340) calc(var(--u) * 320) at 50% calc(var(--u) * -60), rgb(255 255 255 / 0.08), transparent 70%), linear-gradient(#1a1a26, #0d0d1a);
  font-size: calc(var(--t) * 13); animation: cxo-in 0.4s ease-out;
}
.cxo-min { position: absolute; top: calc(var(--u) * 16); right: calc(var(--u) * 16); display: grid; place-items: center; width: calc(var(--u) * 40); height: calc(var(--u) * 40); border-radius: 50%; background: rgb(255 255 255 / 0.08); }
.cxo-screen .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
.cxo-id { display: grid; justify-items: center; margin-top: calc(var(--u) * 92); }
.cxo-pstatus { display: flex; align-items: center; gap: calc(var(--u) * 8); color: rgb(255 255 255 / 0.8); font-weight: 500; }
/* The live dot is the theme primary, pulsing in every state (dark theme: #FAFAFA). */
.cxo-pstatus i { width: calc(var(--u) * 7); height: calc(var(--u) * 7); border-radius: 50%; background: #fafafa; box-shadow: 0 0 calc(var(--u) * 6) rgb(250 250 250 / 0.6); animation: cxo-pulse 1.8s ease-in-out infinite alternate; }
.cxo-avatar { display: grid; place-items: center; width: calc(var(--u) * 88); height: calc(var(--u) * 88); margin-top: calc(var(--u) * 28); border-radius: 50%; background: linear-gradient(135deg, #3a3a4a, #4f46e5); font-size: calc(var(--t) * 32); font-weight: 700; }
.cxo-id b { margin-top: calc(var(--u) * 18); font-size: calc(var(--t) * 24); font-weight: 700; }
.cxo-id small { margin-top: calc(var(--u) * 6); color: rgb(255 255 255 / 0.45); font-size: calc(var(--t) * 14); }
.cxo-pring { display: flex; gap: calc(var(--u) * 80); margin-top: auto; margin-bottom: calc(var(--u) * 64); }
.cxo-round { display: grid; place-items: center; width: calc(var(--u) * 58); height: calc(var(--u) * 58); border-radius: 50%; }
.cxo-round .ic { width: calc(var(--u) * 28); height: calc(var(--u) * 28); }
.cxo-round.red { background: #ef4444; box-shadow: 0 0 calc(var(--u) * 20) rgb(239 68 68 / 0.45); }
.cxo-round.green { background: #22c55e; box-shadow: 0 0 calc(var(--u) * 20) rgb(34 197 94 / 0.45); }
.cxo-pctl { display: grid; grid-template-columns: repeat(3, calc(var(--u) * 72)); justify-content: center; justify-items: center; gap: calc(var(--u) * 18) calc(var(--u) * 26); margin-top: auto; margin-bottom: calc(var(--u) * 44); animation: cxo-in 0.4s ease-out; }
.cxo-pc { display: grid; justify-items: center; gap: calc(var(--u) * 6); color: rgb(255 255 255 / 0.5); font-size: calc(var(--t) * 10); }
.cxo-pc > span { display: grid; place-items: center; width: calc(var(--u) * 48); height: calc(var(--u) * 48); border: 1px solid rgb(255 255 255 / 0.1); border-radius: 50%; background: rgb(255 255 255 / 0.1); color: #fff; }
.cxo-pc.on > span { border-color: transparent; background: rgb(250 250 250 / 0.85); color: #171717; box-shadow: 0 0 calc(var(--u) * 16) rgb(255 255 255 / 0.25); }
.cxo-pc .cxo-rec { width: calc(var(--u) * 16); height: calc(var(--u) * 16); margin: 0; color: #ef4444; }
.cxo-pc .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
.cxo-pend { grid-column: 2; width: calc(var(--u) * 56); height: calc(var(--u) * 56); margin-top: calc(var(--u) * 10); }
.cxo-ptr { display: grid; align-self: stretch; margin: auto calc(var(--u) * 24) calc(var(--u) * 28); animation: cxo-in 0.4s ease-out; }
.cxo-ptr-head { display: flex; align-items: center; gap: calc(var(--u) * 12); font-size: calc(var(--t) * 16); }
.cxo-ptr-head .ic { width: calc(var(--u) * 20); height: calc(var(--u) * 20); color: rgb(255 255 255 / 0.7); }
.cxo-ptr-box { display: grid; justify-items: center; margin-top: calc(var(--u) * 20); border: 1px solid rgb(255 255 255 / 0.12); border-radius: calc(var(--u) * 16); background: rgb(255 255 255 / 0.06); padding: calc(var(--u) * 16); color: #a78bfa; text-align: center; }
.cxo-screen .cxo-ptr-box > .ic { width: calc(var(--u) * 36); height: calc(var(--u) * 36); }
.cxo-ptr-box b { margin-top: calc(var(--u) * 12); font-size: calc(var(--t) * 16); font-weight: 600; }
.cxo-ptr-box small { margin-top: calc(var(--u) * 4); color: rgb(255 255 255 / 0.5); font-size: calc(var(--t) * 13); }
.cxo-ptr-wait { display: flex; align-items: center; gap: calc(var(--u) * 8); margin-top: calc(var(--u) * 12); color: rgb(255 255 255 / 0.5); font-size: calc(var(--t) * 13); }
.cxo-ptr-wait i { width: calc(var(--u) * 12); height: calc(var(--u) * 12); border: calc(var(--u) * 2) solid rgb(167 139 250 / 0.25); border-top-color: #a78bfa; border-radius: 50%; animation: cxo-spin 0.9s linear infinite; }
.cxo-ptr-cancel { display: grid; place-items: center; height: calc(var(--u) * 44); margin-top: calc(var(--u) * 12); border: 1px solid rgb(255 255 255 / 0.24); border-radius: calc(var(--u) * 22); color: rgb(255 255 255 / 0.9); font-size: calc(var(--t) * 14); font-weight: 500; }
.cxo-ptr-end { justify-self: center; width: calc(var(--u) * 56); height: calc(var(--u) * 56); margin-top: calc(var(--u) * 20); }
@keyframes cxo-pulse { from { opacity: 0.4; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .cxo * { animation: none !important; transition: none !important; } }
</style>
